import { Link } from "react-router-dom";
import { ArrowRight, Leaf, TreeDeciduous, PanelTop, Fence, Layers, Sprout, Circle } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";

// Category images - using product images from assets
import grassImage from "@/assets/products/grass-40mm-natural-installed.jpg";
import deckingImage from "@/assets/products/wpc-decking-redbrown-1.jpg";
import claddingImage from "@/assets/products/wpc-cladding-charcoal.jpg";
import indoorCladdingImage from "@/assets/products/uv-marble-alpine-gold-vein-installed.jpg";
import flooringImage from "@/assets/products/spc-european-oak-installed.jpg";
import hedgeImage from "@/assets/products/hedge-boxwood.jpg";
import weedMatImage from "@/assets/products/weed-mat-installed.jpg";
import pebblesImage from "@/assets/products/white-garden-pebbles.jpg";
import { WET_WALL_ASSETS } from "@/data/wetWallAssets";

const wetWallImage = WET_WALL_ASSETS["stone-beige"].primary;


const categories = [
  {
    name: "Artificial Grass",
    description: "Low maintenance, pet-friendly & UV stabilised",
    href: "/artificial-grass",
    image: grassImage,
    icon: Leaf,
    popular: true,
  },
  {
    name: "WPC Decking",
    description: "Durable composite decking for any outdoor space",
    href: "/wpc-decking",
    image: deckingImage,
    icon: PanelTop,
    popular: true,
  },
  {
    name: "WPC External Cladding",
    description: "Weather-resistant exterior wall cladding",
    href: "/wpc-external-cladding",
    image: claddingImage,
    icon: Fence,
    popular: false,
  },
  {
    name: "Supreme Wet Wall Panels",
    description: "Waterproof glossy panels for bathrooms & wet areas",
    href: "/pvc-wet-wall-panels",
    image: wetWallImage,
    icon: PanelTop,
    popular: false,
  },
  {
    name: "Indoor Cladding",
    description: "Elegant interior wall panels & accent walls",
    href: "/indoor-cladding",
    image: indoorCladdingImage,
    icon: Layers,
    popular: false,
  },
  {
    name: "Hybrid Flooring",
    description: "Waterproof, scratch-resistant indoor flooring",
    href: "/hybrid-flooring",
    image: flooringImage,
    icon: Layers,
    popular: false,
  },
  {
    name: "Hedges & Screens",
    description: "Instant privacy with artificial greenery",
    href: "/hedges",
    image: hedgeImage,
    icon: TreeDeciduous,
    popular: false,
  },
  {
    name: "Weed Mats",
    description: "Durable weed control for landscaping & agriculture",
    href: "/weed-mats",
    image: weedMatImage,
    icon: Sprout,
    popular: false,
  },
  {
    name: "Garden Pebbles",
    description: "Premium decorative pebbles for landscaping & pathways",
    href: "/garden-pebbles",
    image: pebblesImage,
    icon: Circle,
    popular: false,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

export function CategoryGrid() {
  return (
    <section className="relative pt-0 pb-16 md:pb-20 bg-background overflow-hidden">
      {/* Smooth wave transition from hero */}
      <div className="absolute -top-1 left-0 right-0 h-16 md:h-24">
        <svg
          viewBox="0 0 1440 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute bottom-0 w-full h-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0 C360,80 1080,80 1440,0 L1440,100 L0,100 Z"
            className="fill-background"
          />
        </svg>
      </div>
      
      <div className="container pt-8 md:pt-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center"
        >
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Shop by Category
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Explore our range of premium building and landscaping materials
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {categories.map((category) => {
            const IconComponent = category.icon;
            
            return (
              <motion.div
                key={category.name}
                variants={cardVariants}
                className="group"
              >
                <Link
                  to={category.href}
                  className="relative block h-64 overflow-hidden rounded-xl shadow-md transition-all duration-500 hover:shadow-xl hover:-translate-y-1"
                >
                  {/* Background Image */}
                  <div className="absolute inset-0">
                    <img
                      src={category.image}
                      alt={category.name}
                      loading="lazy"
                      decoding="async"
                      width={600}
                      height={400}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/50 to-transparent" />
                  
                  {/* Popular Badge */}
                  {category.popular && (
                    <div className="absolute right-3 top-3 z-20">
                      <Badge className="bg-accent text-accent-foreground font-semibold shadow-lg px-3 py-1 text-xs">
                        Popular
                      </Badge>
                    </div>
                  )}

                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-5">
                    {/* Icon */}
                    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm transition-all duration-300 group-hover:bg-accent group-hover:scale-110">
                      <IconComponent className="h-5 w-5 text-white transition-colors duration-300 group-hover:text-accent-foreground" />
                    </div>

                    {/* Title */}
                    <h3 className="mb-1 text-xl font-bold text-white">
                      {category.name}
                    </h3>
                    
                    {/* Description */}
                    <p className="mb-3 text-sm text-white/80 line-clamp-2">
                      {category.description}
                    </p>

                    {/* CTA - appears on hover */}
                    <div className="flex items-center gap-1 text-sm font-semibold text-accent opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:gap-2">
                      <span className="relative">
                        Shop Now
                        <span className="absolute -bottom-0.5 left-0 h-0.5 w-0 bg-accent transition-all duration-300 group-hover:w-full" />
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
